import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import lalafanLogo from '../../assets/images/lalafan_logo.svg';

const StyledHeader = styled.header<{
  $scrolled: boolean;
  $menuOpen: boolean;
  $isHome: boolean;
}>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${({ $menuOpen }) => ($menuOpen ? 10001 : 1000)};
  background: ${({ $scrolled, $menuOpen, $isHome }) =>
    $scrolled || $menuOpen || !$isHome ? '#111111' : 'transparent'};
  backdrop-filter: ${({ $scrolled, $menuOpen, $isHome }) =>
    $scrolled || $menuOpen || !$isHome ? 'blur(12px)' : 'none'};
  transition:
    background 0.3s ease,
    backdrop-filter 0.3s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    background: #111111;
    backdrop-filter: blur(12px);
  }
`;

const Container = styled.div`
  position: relative;
  z-index: 1001;
  max-width: 100%;
  margin: 0 auto;
  padding: 20px 30px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background: #111111;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 8px 20px;
    display: flex;
    justify-content: space-between;
    background: #111111;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 32px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

const NavRight = styled(Nav)`
  justify-content: flex-end;
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  color: ${({ $active, theme }) => ($active ? theme.colors.lime : '#ffffff')};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%; /* 25.2px */
  letter-spacing: -0.45px;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: #d3ee34;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
`;

const HeaderLogo = styled.span<{ $onLight?: boolean }>`
  display: block;
  width: 164px;
  height: 16px;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.colors.lime};
  mask-image: url(${lalafanLogo});
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  -webkit-mask-image: url(${lalafanLogo});
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    background-color: ${({ theme }) => theme.colors.lime};
  }
`;

const MenuButton = styled.button<{ $open: boolean; $onLight?: boolean }>`
  display: none;
  position: relative;
  z-index: 1001;
  width: 36px;
  height: 36px;
  border: 1.5px solid
    ${({ $onLight, $open }) =>
      $open ? '#ffffff' : $onLight ? '#111111' : 'rgba(211, 238, 52, 0.5)'};
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0;

  span {
    display: block;
    width: 18px;
    height: 2px;
    background: ${({ $open, $onLight }) =>
      $open ? '#d3ee34' : $onLight ? '#111111' : '#d3ee34'};
    transition:
      transform 0.2s ease,
      opacity 0.2s ease,
      background 0.2s ease;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
    border-color: rgba(211, 238, 52, 0.5);

    span {
      background: ${({ $open }) => ($open ? '#d3ee34' : '#d3ee34')};
    }
  }
`;

const MobileMenu = styled.nav<{ $open: boolean }>`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
    position: fixed;
    inset: 0;
    z-index: 10000;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 32px;
    padding: 0 20px;
    background: #111111;
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
    pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
    transition:
      opacity 0.25s ease,
      visibility 0.25s ease;
  }
`;

const MobileNavLink = styled(Link)<{ $active?: boolean }>`
  color: ${({ $active, theme }) => ($active ? theme.colors.lime : '#ffffff')};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -1.2px;
  text-decoration: none;
  text-align: center;
`;

const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
};

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  const isHome = location.pathname === '/';
  const isContents = location.pathname === '/contents';
  const isCampaigns = location.pathname === '/campaigns';

  const handleHomeClick = (event: React.MouseEvent) => {
    if (isHome) {
      event.preventDefault();
      scrollToSection('hero');
    } else {
      navigate('/');
    }
    setMenuOpen(false);
  };

  const mobileMenu = (
    <MobileMenu $open={menuOpen} aria-hidden={!menuOpen}>
      <MobileNavLink to="/" $active={isHome} onClick={handleHomeClick}>
        HOME
      </MobileNavLink>
      <MobileNavLink
        to="/contents"
        $active={isContents}
        onClick={() => setMenuOpen(false)}
      >
        CONTENTS
      </MobileNavLink>
      <MobileNavLink
        to="/campaigns"
        $active={isCampaigns}
        onClick={() => setMenuOpen(false)}
      >
        CAMPAIGNS
      </MobileNavLink>
    </MobileMenu>
  );

  const useLightHeader = isHome && !isScrolled && !menuOpen;

  return (
    <StyledHeader
      $scrolled={isScrolled || !isHome}
      $menuOpen={menuOpen}
      $isHome={isHome}
    >
      <Container>
        <LogoLink to="/" onClick={handleHomeClick}>
          <HeaderLogo
            $onLight={useLightHeader}
            role="img"
            aria-label="LALAFAN"
          />
        </LogoLink>
        <NavRight>
          <NavLink to="/" $active={isHome} onClick={handleHomeClick}>
            HOME
          </NavLink>
          <NavLink to="/contents" $active={isContents}>
            CONTENTS
          </NavLink>
          <NavLink to="/campaigns" $active={isCampaigns}>
            CAMPAIGNS
          </NavLink>
        </NavRight>

        <MenuButton
          type="button"
          aria-label={menuOpen ? '메뉴 닫기' : '메뉴'}
          aria-expanded={menuOpen}
          $open={menuOpen}
          $onLight={useLightHeader}
          onClick={() => setMenuOpen((prev) => !prev)}
        >
          <span
            style={{
              transform: menuOpen ? 'translateY(7px) rotate(45deg)' : undefined,
            }}
          />
          <span style={{ opacity: menuOpen ? 0 : 1 }} />
          <span
            style={{
              transform: menuOpen
                ? 'translateY(-7px) rotate(-45deg)'
                : undefined,
            }}
          />
        </MenuButton>
      </Container>

      {createPortal(mobileMenu, document.body)}
    </StyledHeader>
  );
};

export default Header;
